/**
 * IRLNotesAdder — paste call notes, Claude extracts the follow-up IRL items,
 * user reviews them on a card list, approved items append to the project's
 * IRL.
 *
 * Mounted inline inside IRLReviewPanel via a header button. Two pages:
 *   1) Paste — textarea + Extract
 *   2) Review — one card per candidate with editable cells, the source
 *      quote that triggered it, and a checkbox to include/exclude.
 *
 * On save we APPEND to the existing IRL (no replace) and call onAdded so
 * the parent refreshes its in-state requests list.
 */

import React, { useMemo, useState } from "react";
import type { IRLPriority, IRLRequest } from "../types";
import { getIRLMeta, getIRLRequests, saveIRLRequests } from "../store/irlStore";
import { apiFetch } from "../../api/fddClient";

type Props = {
  projectId: string;
  /** Called after rows are appended; parent re-reads requests/meta from the store. */
  onAdded: (addedIds: string[]) => void;
  /** Called when the user backs out without saving. */
  onCancel: () => void;
};

type Extracted = {
  topic: string;
  entityName: string;
  request: string;
  priority: IRLPriority;
  sourceQuote: string;
  confidence: "high" | "medium" | "low";
};

type EditableRow = Extracted & {
  /** Local row id for React keying / selection. */
  rowId: string;
  /** Auto-generated IRL # — user can override. */
  number: string;
};

const CONFIDENCE_STYLE: Record<Extracted["confidence"], { bg: string; fg: string; label: string }> = {
  high: { bg: "#dff3d9", fg: "#15803d", label: "HIGH" },
  medium: { bg: "#fff4e6", fg: "#9a3412", label: "MED" },
  low: { bg: "#f1f5f9", fg: "#475569", label: "LOW" },
};

const PRIORITY_OPTIONS: { value: IRLPriority; label: string; bg: string; fg: string }[] = [
  { value: "high", label: "High", bg: "#fee2e2", fg: "#991b1b" },
  { value: "medium", label: "Medium", bg: "#fef9c3", fg: "#854d0e" },
  { value: "low", label: "Low", bg: "#e0f2fe", fg: "#075985" },
];

const STYLE_EXAMPLE_LIMIT = 15;

/**
 * How many existing requests to send as the dedup target. The server already
 * truncates to the same number before it builds the prompt
 * (`server/irl-extract-from-notes/prompts/extract-from-notes.ts`), so sending
 * more was pure waste — and on a mature IRL it pushed the request body past
 * the 128KB that Front Door's WAF inspects, which is answered with a bare 403
 * at the edge. That surfaced in production as "Extract failed (HTTP 403)" with
 * nothing in the server log, because the request never reached the server.
 * Keep this in step with the server-side slice.
 */
const DEDUP_TARGET_LIMIT = 200;

function pickStyleExamples(requests: IRLRequest[]): IRLRequest[] {
  if (requests.length <= STYLE_EXAMPLE_LIMIT) return requests;
  // Diverse sample: prefer one per topic, then fill with the rest.
  const seen = new Set<string>();
  const diverse: IRLRequest[] = [];
  for (const r of requests) {
    const key = (r.topic ?? "").toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      diverse.push(r);
    }
    if (diverse.length >= STYLE_EXAMPLE_LIMIT) break;
  }
  if (diverse.length < STYLE_EXAMPLE_LIMIT) {
    for (const r of requests) {
      if (diverse.includes(r)) continue;
      diverse.push(r);
      if (diverse.length >= STYLE_EXAMPLE_LIMIT) break;
    }
  }
  return diverse;
}

function distinct(values: (string | undefined)[]): string[] {
  const set = new Set<string>();
  for (const v of values) {
    const t = (v ?? "").trim();
    if (t) set.add(t);
  }
  return Array.from(set).sort();
}

/**
 * Compute the starting integer for new IRL numbers based on what's already in
 * the list. Parses the leading integer of each existing number and picks
 * max+1. Hierarchical numbers like "1.3.1" round up to the next top-level int.
 */
function nextStartingNumber(requests: IRLRequest[]): number {
  let max = 0;
  for (const r of requests) {
    const m = /^\s*(\d+)/.exec(r.number ?? "");
    if (m) {
      const n = parseInt(m[1], 10);
      if (!isNaN(n) && n > max) max = n;
    }
  }
  return max + 1;
}

const IRLNotesAdder: React.FC<Props> = ({ projectId, onAdded, onCancel }) => {
  const [page, setPage] = useState<"paste" | "review">("paste");
  const [notes, setNotes] = useState("");
  const [extracting, setExtracting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<EditableRow[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Pulled fresh on each Extract click — no need to recompute on every render.
  const buildPayload = () => {
    const existing = getIRLRequests(projectId);
    return {
      notes: notes.trim(),
      styleExamples: pickStyleExamples(existing).map((r) => ({
        number: r.number,
        topic: r.topic,
        entityName: r.entityName,
        request: r.request,
      })),
      topicsInUse: distinct(existing.map((r) => r.topic)),
      entitiesInUse: distinct(existing.map((r) => r.entityName)),
      existingRequests: existing.slice(0, DEDUP_TARGET_LIMIT).map((r) => r.request),
    };
  };

  const handleExtract = async () => {
    setError(null);
    if (notes.trim().length < 20) {
      setError("Paste your call notes first (at least a sentence or two).");
      return;
    }
    setExtracting(true);
    try {
      const res = await apiFetch("/api/irl/extract-from-notes", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(buildPayload()),
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody?.error || `Extract failed (HTTP ${res.status})`);
      }
      const data = (await res.json()) as { items: Extracted[] };
      const items = Array.isArray(data.items) ? data.items : [];
      if (items.length === 0) {
        setError(
          'No follow-ups detected. If your notes had follow-ups, try tagging them more clearly (e.g., add "- IRL" or "follow up:" near the line).',
        );
        return;
      }
      const start = nextStartingNumber(getIRLRequests(projectId));
      const editable: EditableRow[] = items.map((it, i) => ({
        ...it,
        rowId: `extract-${Date.now()}-${i}`,
        number: String(start + i),
      }));
      setRows(editable);
      // Strict extraction rules — everything that came back is explicit, so
      // pre-check all. User can uncheck individual rows in review.
      setSelected(new Set(editable.map((r) => r.rowId)));
      setPage("review");
    } catch (e: any) {
      setError(e?.message || "Extraction failed.");
    } finally {
      setExtracting(false);
    }
  };

  const updateRow = (rowId: string, patch: Partial<EditableRow>) => {
    setRows((prev) => prev.map((r) => (r.rowId === rowId ? { ...r, ...patch } : r)));
  };

  const toggleRow = (rowId: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });

  const allSelected = rows.length > 0 && selected.size === rows.length;
  const someSelected = selected.size > 0 && !allSelected;
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.rowId)));
  };

  const handleSave = () => {
    if (selected.size === 0) {
      setError("Check at least one follow-up to add.");
      return;
    }
    const existing = getIRLRequests(projectId);
    const meta = getIRLMeta(projectId);
    const fileName = meta?.originalFileName || "Notes-derived IRL";

    const additions: IRLRequest[] = rows
      .filter((r) => selected.has(r.rowId))
      .map((r, i) => ({
        id: `notes-${Date.now()}-${i}`,
        number: r.number.trim() || String(nextStartingNumber(existing) + i),
        topic: r.topic.trim() || undefined,
        entityName: r.entityName.trim() || undefined,
        request: r.request.trim(),
        status: "open" as const,
        priority: r.priority,
      }));

    saveIRLRequests(projectId, [...existing, ...additions], fileName);
    onAdded(additions.map((a) => a.id));
  };

  /* ─────────────────────────── Render: paste page ─────────────────────────── */
  if (page === "paste") {
    return (
      <div style={containerStyle}>
        <div style={headerStyle}>
          <div>
            <div style={{ fontWeight: 700, color: "#2f6b2f", fontSize: 14 }}>
              📝 Add IRL items from call notes
            </div>
            <div style={{ fontSize: 12, color: "#4a7d4a", marginTop: 4, lineHeight: 1.5 }}>
              Claude only picks up lines you've explicitly tagged. Add one of these markers next
              to anything you want pulled into the IRL:
              <ul style={{ margin: "6px 0 0 0", paddingLeft: 18 }}>
                <li>
                  <code style={inlineCode}>IRL</code> — e.g. "AP aging Dec-23 — IRL"
                </li>
                <li>
                  <code style={inlineCode}>follow up</code> / <code style={inlineCode}>FU</code>{" "}
                  — e.g. "Mgmt to send Q4 lease (FU)"
                </li>
                <li>
                  <code style={inlineCode}>action item</code> /{" "}
                  <code style={inlineCode}>AI:</code> — e.g. "AI: confirm working capital
                  adjustment"
                </li>
                <li>
                  <code style={inlineCode}>circle back</code> — e.g. "Circle back on customer
                  concentration"
                </li>
              </ul>
              <div style={{ marginTop: 6 }}>
                Lines without a marker are ignored, even if they sound like commitments
                ("we will…", "to confirm…"). Claude polishes the phrasing but won't invent
                details that aren't in your note.
              </div>
            </div>
          </div>
          <button onClick={onCancel} style={secondaryBtn}>
            Cancel
          </button>
        </div>

        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={14}
          placeholder={`Example —

Discussed Q3 results with the CFO. Margins look fine but A/R has stretched.

- AP aging detail by vendor as of 9/30/2024 - IRL
- Monthly bank recs for the last 12 months (FU)
- Need to ask about the new lease at NewCo, follow up

CFO walked through cash flow forecasting methodology — already received the model.`}
          style={{
            width: "100%",
            padding: "12px 14px",
            border: "1px solid #cfe6c7",
            borderRadius: 8,
            fontSize: 13,
            fontFamily: "inherit",
            lineHeight: 1.5,
            background: "#fff",
            resize: "vertical",
            minHeight: 280,
            boxSizing: "border-box",
          }}
        />

        {error && <div style={errorStyle}>{error}</div>}

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12 }}>
          <div style={{ fontSize: 11, color: "#6b7280" }}>
            {notes.trim().length > 0 ? `${notes.trim().split(/\s+/).length} words` : "no text yet"}
          </div>
          <button
            onClick={handleExtract}
            disabled={extracting || notes.trim().length < 20}
            style={{
              ...primaryBtn,
              background: extracting || notes.trim().length < 20 ? "#9bbf8f" : "#15803d",
              cursor: extracting || notes.trim().length < 20 ? "not-allowed" : "pointer",
            }}
          >
            {extracting ? "Extracting…" : "✨ Extract follow-ups"}
          </button>
        </div>
      </div>
    );
  }

  /* ─────────────────────────── Render: review page ─────────────────────────── */
  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <div>
          <div style={{ fontWeight: 700, color: "#2f6b2f", fontSize: 14 }}>
            Review extracted follow-ups
          </div>
          <div style={{ fontSize: 12, color: "#4a7d4a", marginTop: 2 }}>
            {rows.length} candidate{rows.length === 1 ? "" : "s"} · {selected.size} selected ·
            edit any field before adding
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => setPage("paste")} style={secondaryBtn}>
            ← Back
          </button>
          <button
            onClick={handleSave}
            disabled={selected.size === 0}
            style={{
              ...primaryBtn,
              background: selected.size === 0 ? "#9bbf8f" : "#15803d",
              cursor: selected.size === 0 ? "not-allowed" : "pointer",
            }}
          >
            ✅ Add {selected.size} to IRL
          </button>
        </div>
      </div>

      {/* Select-all bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "6px 10px",
          background: "#f4fbf1",
          border: "1px solid #cfe6c7",
          borderRadius: 6,
          fontSize: 12,
          color: "#4a7d4a",
        }}
      >
        <input
          type="checkbox"
          checked={allSelected}
          ref={(el) => {
            if (el) el.indeterminate = someSelected;
          }}
          onChange={toggleAll}
          style={{ cursor: "pointer", margin: 0 }}
        />
        <span>{allSelected ? "Uncheck all" : "Select all"}</span>
        <span style={{ marginLeft: "auto", color: "#6b7280" }}>
          All explicit follow-ups are pre-selected. Uncheck any you don't want.
        </span>
      </div>

      {/* Cards */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 10 }}>
        {rows.map((r) => {
          const isOn = selected.has(r.rowId);
          const cs = CONFIDENCE_STYLE[r.confidence];
          return (
            <div
              key={r.rowId}
              style={{
                border: `1px solid ${isOn ? "#cfe6c7" : "#e5e7eb"}`,
                borderRadius: 10,
                padding: 12,
                background: isOn ? "#fbfffa" : "#f9fafb",
                opacity: isOn ? 1 : 0.7,
                display: "flex",
                gap: 12,
              }}
            >
              <div style={{ paddingTop: 4 }}>
                <input
                  type="checkbox"
                  checked={isOn}
                  onChange={() => toggleRow(r.rowId)}
                  style={{ cursor: "pointer", margin: 0, width: 16, height: 16 }}
                />
              </div>

              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}>
                {/* Top row: IRL # + topic + entity + confidence */}
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  <FieldLabel label="IRL #" width={80}>
                    <input
                      type="text"
                      value={r.number}
                      onChange={(e) => updateRow(r.rowId, { number: e.target.value })}
                      style={{ ...rowInputStyle, fontFamily: "monospace", width: "100%" }}
                    />
                  </FieldLabel>
                  <FieldLabel label="Topic" width={170}>
                    <input
                      type="text"
                      value={r.topic}
                      onChange={(e) => updateRow(r.rowId, { topic: e.target.value })}
                      placeholder="—"
                      style={{ ...rowInputStyle, width: "100%" }}
                    />
                  </FieldLabel>
                  <FieldLabel label="Entity" width={200}>
                    <input
                      type="text"
                      value={r.entityName}
                      onChange={(e) => updateRow(r.rowId, { entityName: e.target.value })}
                      placeholder="(none)"
                      style={{ ...rowInputStyle, width: "100%" }}
                    />
                  </FieldLabel>
                  <FieldLabel label="Priority" width={110}>
                    {(() => {
                      const opt = PRIORITY_OPTIONS.find((o) => o.value === r.priority) ?? PRIORITY_OPTIONS[1];
                      return (
                        <select
                          value={r.priority}
                          onChange={(e) => updateRow(r.rowId, { priority: e.target.value as IRLPriority })}
                          style={{
                            ...rowInputStyle,
                            width: "100%",
                            background: opt.bg,
                            color: opt.fg,
                            fontWeight: 700,
                          }}
                        >
                          {PRIORITY_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      );
                    })()}
                  </FieldLabel>
                  <span
                    style={{
                      marginLeft: "auto",
                      background: cs.bg,
                      color: cs.fg,
                      padding: "2px 10px",
                      borderRadius: 999,
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: 0.5,
                    }}
                  >
                    {cs.label}
                  </span>
                </div>

                {/* Request */}
                <FieldLabel label="Request">
                  <textarea
                    value={r.request}
                    onChange={(e) => updateRow(r.rowId, { request: e.target.value })}
                    rows={2}
                    style={{
                      ...rowInputStyle,
                      width: "100%",
                      resize: "vertical",
                      minHeight: 48,
                      lineHeight: 1.4,
                      fontFamily: "inherit",
                    }}
                  />
                </FieldLabel>

                {/* Source quote */}
                {r.sourceQuote && (
                  <div
                    style={{
                      fontSize: 11,
                      color: "#6b7280",
                      fontStyle: "italic",
                      borderLeft: "2px solid #cfe6c7",
                      paddingLeft: 8,
                      lineHeight: 1.4,
                    }}
                  >
                    from notes: <span style={{ color: "#374151" }}>{`"${r.sourceQuote}"`}</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {error && <div style={{ ...errorStyle, marginTop: 12 }}>{error}</div>}
    </div>
  );
};

/* ─────────────────────────── Style helpers ─────────────────────────── */

const containerStyle: React.CSSProperties = {
  background: "#ffffff",
  border: "1px solid #cfe6c7",
  borderRadius: 10,
  padding: 14,
  display: "flex",
  flexDirection: "column",
  gap: 10,
};

const headerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: 12,
  flexWrap: "wrap",
};

const primaryBtn: React.CSSProperties = {
  background: "#15803d",
  color: "#fff",
  border: "none",
  borderRadius: 8,
  padding: "8px 14px",
  fontWeight: 700,
  fontSize: 12,
};

const secondaryBtn: React.CSSProperties = {
  background: "#fff",
  color: "#4a7d4a",
  border: "1px solid #cfe6c7",
  borderRadius: 6,
  padding: "6px 12px",
  cursor: "pointer",
  fontSize: 12,
  fontWeight: 600,
};

const rowInputStyle: React.CSSProperties = {
  padding: "5px 8px",
  border: "1px solid #cfe6c7",
  borderRadius: 5,
  fontSize: 12,
  background: "#fff",
  color: "#333",
  boxSizing: "border-box",
};

const inlineCode: React.CSSProperties = {
  fontFamily: "monospace",
  fontSize: 11,
  background: "#eaf7e6",
  padding: "1px 4px",
  borderRadius: 3,
  color: "#15803d",
};

const errorStyle: React.CSSProperties = {
  background: "#fef2f2",
  border: "1px solid #fca5a5",
  borderRadius: 8,
  padding: "8px 12px",
  fontSize: 12,
  color: "#991b1b",
};

const FieldLabel: React.FC<{
  label: string;
  width?: number;
  children: React.ReactNode;
}> = ({ label, width, children }) => (
  <label style={{ display: "flex", flexDirection: "column", gap: 2, width: width ?? "auto", flex: width ? "0 0 auto" : 1 }}>
    <span style={{ fontSize: 10, fontWeight: 700, color: "#4a7d4a", textTransform: "uppercase", letterSpacing: 0.4 }}>
      {label}
    </span>
    {children}
  </label>
);

export default IRLNotesAdder;
